import { Button, Field, Fieldset, HStack, Text } from '@chakra-ui/react';
import CustomInputDateTimeRange from '@components/common/inputs/CustomInputDateTimeRange';
import { useReplayTimelineContext } from '@components/replay/ReplayTimelineContext';
import { DraggablePanel } from '@components/ui/draggable';
import { Form } from '@components/ui/Form';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { closePopupByMapIdAndType } from '@redux/maps/maps.reducer';
import { selectReplayUuid } from '@redux/replay/replay.selectors';
import { useUpdateStreamMutation } from '@services/replay/replay.api';
import { getNewTimelineEndAndTimeScaleFromRange } from '@utils/replay/replay.utils';
import { dateSchema } from '@validations/date.schema';
import { FastField, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';

import { DateTimeRange } from '@/types/dateTime.types';
import { PopupTypeEnum } from '@/types/popup.types';

type Props = {
  position: { x: number; y: number };
};

export default function ReplayDatePopupForm({ position }: Readonly<Props>) {
  const validationSchema = useValidation(dateSchema);
  const { mapId, range, setDateRange, setTimeScale } = useReplayTimelineContext();
  const init = { dateRange: range };
  const closePopup = useWithDispatch(closePopupByMapIdAndType);
  const replayUuid = useAppSelector(selectReplayUuid);
  const [updateStream] = useUpdateStreamMutation();

  const handleSubmit = (values: { dateRange: DateTimeRange }) => {
    const { newEndTime, newTimeScale } = getNewTimelineEndAndTimeScaleFromRange(values.dateRange);
    setTimeScale(newTimeScale);
    setDateRange(values.dateRange.from, newEndTime);
    if (replayUuid) {
      updateStream({ uuid: replayUuid, command: { time: values.dateRange.from, speed: 0 } });
    }
    closePopup({ mapId, type: PopupTypeEnum.REPLAY });
  };

  return (
    <DraggablePanel.Root top={position.y} left={position.x} offsetY={-300}>
      <DraggablePanel.Header onClose={() => closePopup({ mapId, type: PopupTypeEnum.REPLAY })}>
        <FormattedMessage id="replay.search.datePopup.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={850}>
        <Formik initialValues={init} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ errors, touched, setTouched, dirty, isValid, values, setFieldValue }) => (
            <Fieldset.Root
              padding={3}
              invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}
            >
              <Text>
                <FormattedMessage id="replay.search.datePopup.description" />
              </Text>
              <Form>
                <Field.Root marginTop={2} invalid={touched.dateRange && !!errors.dateRange}>
                  <FastField
                    as={CustomInputDateTimeRange}
                    id="dateRange"
                    name="dateRange"
                    range={values.dateRange}
                    onChange={async (range: DateTimeRange) => {
                      await setTouched({ dateRange: { from: true, to: true } });
                      await setFieldValue('dateRange', range);
                    }}
                  />
                </Field.Root>
                <Fieldset.ErrorText>
                  {!!errors.dateRange?.from && <Text>{errors.dateRange.from as string}</Text>}
                  {!!errors.dateRange?.to && <Text>{errors.dateRange.to as string}</Text>}
                </Fieldset.ErrorText>

                <HStack alignItems="center" justify="center" margin={1} marginTop={3} gap="20px" width="100%">
                  <Button
                    type="button"
                    variant="secondary"
                    flex={1}
                    onClick={() => closePopup({ mapId, type: PopupTypeEnum.REPLAY })}
                  >
                    <FormattedMessage id="global.cancel" />
                  </Button>
                  <Button type="submit" flex={1} disabled={!dirty || !isValid}>
                    <FormattedMessage id="replay.search.datePopup.validate" />
                  </Button>
                </HStack>
              </Form>
            </Fieldset.Root>
          )}
        </Formik>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
